import { useTranslation } from 'react-i18next'

import { Avatar, DueDate, PriorityFlag, StatusPill } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { NewTask } from '@/features/work-items/NewTask'
import type { WorkItem } from '@/types/work'

/**
 * The dense list view.
 *
 * Rows rather than cards, because the point of a list is comparison: status in
 * the same column every time, the assignee in the same place, a date that turns
 * red when it is behind. Grouped by status so the blocked work is a block you
 * can see rather than rows you have to find.
 */
export function TaskList({
  items,
  asOf,
  onOpen,
  projectId,
  showProject = false,
}: {
  items: WorkItem[]
  asOf: number
  onOpen: (item: WorkItem) => void
  /** Given, every group gets a composer. Absent — My Work, which spans projects — none do. */
  projectId?: string | undefined
  showProject?: boolean
}) {
  const { t } = useTranslation()

  if (items.length === 0) {
    return (
      <div className="border-line bg-surface rounded-md border">
        <p className="text-ink-700 px-4 py-3 text-sm">{t('work.noItems')}</p>
        {projectId === undefined ? null : (
          <div className="border-line border-t">
            <NewTask projectId={projectId} />
          </div>
        )}
      </div>
    )
  }

  const groups = new Map<string, WorkItem[]>()
  for (const item of items) {
    const key = item.state?.name ?? t('work.noItems')
    groups.set(key, [...(groups.get(key) ?? []), item])
  }

  return (
    <div className="flex flex-col gap-5">
      {[...groups.entries()].map(([status, rows]) => (
        <section key={status}>
          <div className="mb-1.5 flex items-center gap-2">
            <StatusPill
              name={status}
              category={rows[0]?.state?.category ?? null}
              compact
            />
            <span className="text-ink-500 font-mono text-[11px] tabular-nums">{rows.length}</span>
          </div>

          <ul className="border-line bg-surface divide-line divide-y overflow-hidden rounded-md border">
            {rows.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className="hover:bg-surface-2 group flex w-full items-center gap-3 px-3 py-2 text-start"
                >
                  <PriorityFlag priority={item.priority} />

                  <span className="text-ink-900 min-w-0 flex-1 truncate text-[13px]">
                    {item.title}
                  </span>

                  {item.commentCount > 0 ? (
                    <span className="text-ink-500 flex items-center gap-0.5 font-mono text-[10px]">
                      <Icon name="comment" className="size-3" />
                      {item.commentCount}
                    </span>
                  ) : null}

                  {showProject && item.project !== undefined ? (
                    <span className="text-ink-500 hidden truncate font-mono text-[10px] sm:block sm:w-28">
                      {item.project.key}
                    </span>
                  ) : null}

                  <span className="w-20 text-end">
                    <DueDate date={item.dueDate} asOf={asOf} />
                  </span>

                  <Avatar
                    name={item.assignee?.name ?? null}
                    color={item.assignee?.color}
                    size="sm"
                  />

                  <span className="text-brand-600 w-20 shrink-0 text-end font-mono text-[10px]">
                    {item.key}
                  </span>
                </button>
              </li>
            ))}

            {projectId === undefined ? null : (
              <li>
                <NewTask projectId={projectId} stateId={rows[0]?.state?.id} />
              </li>
            )}
          </ul>
        </section>
      ))}
    </div>
  )
}
